import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type ApprovalStatus = "pending" | "approved" | "rejected" | null;
export type AppRole = Database["public"]["Enums"]["app_role"];

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAdmin: boolean;
  approvalStatus: ApprovalStatus;
  userRoles: AppRole[];
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, displayName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>(null);
  const [userRoles, setUserRoles] = useState<AppRole[]>([]);

  useEffect(() => {
    let isMounted = true;

    const checkAdminRole = async (userId: string) => {
      try {
        const { data, error } = await supabase.rpc('is_admin_or_editor', { 
          _user_id: userId 
        });
        if (error) {
          console.error('Error checking admin role:', error);
          return false;
        }
        return data ?? false;
      } catch (err) {
        console.error('Failed to check admin role:', err);
        return false;
      }
    };

    const checkApprovalStatus = async (userId: string): Promise<ApprovalStatus> => {
      try {
        const { data, error } = await supabase.rpc('get_user_approval_status', { 
          _user_id: userId 
        });
        if (error) {
          console.error('Error checking approval status:', error);
          return null;
        }
        return data as ApprovalStatus;
      } catch (err) {
        console.error('Failed to check approval status:', err);
        return null;
      }
    };

    const getUserRoles = async (userId: string): Promise<AppRole[]> => {
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId);
        
        if (error) {
          console.error('Error fetching user roles:', error);
          return [];
        }
        return data.map(r => r.role);
      } catch (err) {
        console.error('Failed to fetch user roles:', err);
        return [];
      }
    };

    const refreshUserStatus = (userId: string) => {
      setTimeout(async () => {
        const [adminStatus, approval, roles] = await Promise.all([
          checkAdminRole(userId),
          checkApprovalStatus(userId),
          getUserRoles(userId)
        ]);
        if (isMounted) {
          setIsAdmin(adminStatus);
          setApprovalStatus(approval);
          setUserRoles(roles);
        }
      }, 0);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;

        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);

        if (session?.user) {
          refreshUserStatus(session.user.id);
        } else {
          setIsAdmin(false);
          setApprovalStatus(null);
          setUserRoles([]);
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!isMounted) return;

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        const [adminStatus, approval, roles] = await Promise.all([
          checkAdminRole(session.user.id),
          checkApprovalStatus(session.user.id),
          getUserRoles(session.user.id)
        ]);
        if (isMounted) {
          setIsAdmin(adminStatus);
          setApprovalStatus(approval);
          setUserRoles(roles);
        }
      } else {
        setIsAdmin(false);
        setApprovalStatus(null);
        setUserRoles([]);
      }

      setIsLoading(false);
    }).catch((err) => {
      console.error('Failed to get session:', err);
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { display_name: displayName }
      }
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, isLoading, isAdmin, approvalStatus, userRoles, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
